
// frontend/src/components/Sports/LiveMatchTicker.js
// Elite marquee-style ticker - clean edges, no color bleeding
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSportsScores } from '../../services/SportsDataService';
import { Radio, ChevronRight, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';

// Compact match display
const MatchItem = ({ event, leagueName, leaguePath }) => {
    const competition = event.competitions[0];
    const status = competition.status.type;
    const home = competition.competitors.find(c => c.homeAway === 'home');
    const away = competition.competitors.find(c => c.homeAway === 'away');
    const isLive = status.state === 'in';
    const isCompleted = status.state === 'post';

    return (
        <Link
            to={leaguePath}
            className="inline-flex items-center gap-1.5 px-3 whitespace-nowrap hover:text-primary transition-colors text-[11px]"
        >
            <span className="text-muted-foreground/70 font-medium uppercase text-[10px]">{leagueName}</span>
            <span className="text-white/90 font-medium">{home?.team?.abbreviation}</span>
            <span className={`font-bold ${isLive ? 'text-red-400' : 'text-primary'}`}>
                {home?.score ?? '-'}
            </span>
            <span className="text-white/30">-</span>
            <span className={`font-bold ${isLive ? 'text-red-400' : 'text-primary'}`}>
                {away?.score ?? '-'}
            </span>
            <span className="text-white/90 font-medium">{away?.team?.abbreviation}</span>
            {isLive && <span className="text-red-400 font-bold animate-pulse text-[10px]">LIVE</span>}
            {isCompleted && <span className="text-muted-foreground/60 text-[10px]">FT</span>}
            {!isLive && !isCompleted && <span className="text-muted-foreground/60 text-[10px]">{status.shortDetail?.slice(0, 10)}</span>}
            <span className="text-white/10 ml-1">•</span>
        </Link>
    );
};

// Hook to get all matches from a league
const useLeagueMatches = (sport, league, name, path) => {
    const { games, isLoading } = useSportsScores(sport, league);

    if (isLoading || !games || games.length === 0) return [];

    return games.slice(0, 2).map(game => ({
        ...game,
        leagueName: name,
        leaguePath: path
    }));
};

export const LiveMatchTicker = () => {
    const [allMatches, setAllMatches] = useState([]);
    const [isScrolled, setIsScrolled] = useState(false);

    // Track scroll to hide ticker when nav compresses
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 100);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Fetch from all leagues
    const epl = useLeagueMatches('soccer', 'eng.1', 'EPL', '/sports/soccer/premier-league');
    const nba = useLeagueMatches('basketball', 'nba', 'NBA', '/sports/basketball/nba');
    const nfl = useLeagueMatches('football', 'nfl', 'NFL', '/sports/football/nfl');
    const laliga = useLeagueMatches('soccer', 'esp.1', 'LaLiga', '/sports/soccer/la-liga');

    useEffect(() => {
        const combined = [...epl, ...nba, ...nfl, ...laliga];
        setAllMatches(combined);
    }, [epl.length, nba.length, nfl.length, laliga.length]);

    const hasMatches = allMatches.length > 0;

    return (
        <AnimatePresence>
            {!isScrolled && (
                <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                >
                    {/* Outer wrapper - same margins as nav */}
                    <div className="px-4 sm:px-6 lg:px-0">
                        {/* Outer container - solid dark background with rounded corners */}
                        <div
                            className="mx-auto"
                            style={{
                                maxWidth: '1500px',
                                background: '#0a0a0a',
                                borderRadius: '0 0 12px 12px',
                                padding: '1px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                borderTop: 'none',
                            }}
                        >
                            {/* Inner container - all content */}
                            <div
                                className="flex items-center"
                                style={{
                                    background: 'rgba(255,255,255,0.03)',
                                    borderRadius: '0 0 11px 11px',
                                }}
                            >
                                {/* LIVE Label - Left side with internal margin */}
                                <div className="flex items-center gap-1.5 px-4 py-2 flex-shrink-0">
                                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse shadow-[0_0_6px_rgba(239,68,68,0.8)]" />
                                    <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest">Live</span>
                                    <div className="w-px h-4 bg-white/10 ml-2" />
                                </div>

                                {/* Marquee Container with fade edges */}
                                <div className="flex-1 overflow-hidden relative py-2">
                                    {/* Left fade gradient */}
                                    <div
                                        className="absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
                                        style={{ background: 'linear-gradient(90deg, rgba(10,10,10,0.95) 0%, transparent 100%)' }}
                                    />

                                    {/* Right fade gradient */}
                                    <div
                                        className="absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none"
                                        style={{ background: 'linear-gradient(270deg, rgba(10,10,10,0.95) 0%, transparent 100%)' }}
                                    />

                                    {hasMatches ? (
                                        <div className="animate-marquee inline-flex items-center">
                                            {allMatches.map((match, i) => (
                                                <MatchItem
                                                    key={`a-${match.id || i}`}
                                                    event={match}
                                                    leagueName={match.leagueName}
                                                    leaguePath={match.leaguePath}
                                                />
                                            ))}
                                            {allMatches.map((match, i) => (
                                                <MatchItem
                                                    key={`b-${match.id || i}`}
                                                    event={match}
                                                    leagueName={match.leagueName}
                                                    leaguePath={match.leaguePath}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-[11px] text-muted-foreground/70 px-4 py-0.5">
                                            <Link to="/sports" className="hover:text-primary transition-colors">
                                                ⚽ Premier League • 🏀 NBA • 🏈 NFL • ⚽ La Liga — Click for live scores
                                            </Link>
                                        </div>
                                    )}
                                </div>

                                {/* Sports Hub Link - Right side with internal margin */}
                                <Link
                                    to="/sports"
                                    className="hidden sm:flex items-center gap-1.5 px-4 py-2 text-[10px] font-bold transition-all flex-shrink-0 group"
                                >
                                    <div className="w-px h-4 bg-white/10 mr-2" />
                                    <Trophy className="w-3 h-3 text-primary group-hover:scale-110 transition-transform" />
                                    <span className="text-primary group-hover:text-white transition-colors">Sports Hub</span>
                                    <ChevronRight className="w-3 h-3 text-primary/70 group-hover:translate-x-0.5 transition-transform" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Marquee Animation CSS */}
                    <style>{`
                        @keyframes marquee {
                            0% { transform: translateX(0); }
                            100% { transform: translateX(-50%); }
                        }
                        .animate-marquee {
                            animation: marquee 30s linear infinite;
                        }
                        .animate-marquee:hover {
                            animation-play-state: paused;
                        }
                    `}</style>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
